import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoginModel } from 'src/app/models/loginModel';
import { RegisterModel } from 'src/app/models/registerModel';
import { SessionService } from 'src/app/services/session-service';
import { PendingFavourite, PendingFavouriteService } from 'src/app/services/pending-favourite-service';
import { getOAuthApiOrigin } from 'src/environments/oauth-api-origin';

export class SignUpCallback {
  constructor() { }
}

@Component({
  selector: 'pla-quick-sign-up-modal',
  templateUrl: './quick-sign-up-modal.component.html',
  styleUrls: ['./quick-sign-up-modal.component.scss'],
})
export class QuickSignUpModalComponent implements OnInit {
  public registerScreen: boolean = true;

  // SOCIAL
  public socialError: string = '';

  // REGISTER
  public registerModel: RegisterModel = new RegisterModel();
  public registerErrors: string[] = [];
  public registerForm = new FormGroup({
    email: new FormControl(this.registerModel.email, [
      Validators.required,
      Validators.email,
    ]),
    password: new FormControl(this.registerModel.password, [
      Validators.required,
    ]),
    passwordRepeat: new FormControl(this.registerModel.passwordRepeat, [
      Validators.required,
    ]),
  });

  // LOGIN
  public loginModel: LoginModel = new LoginModel();
  public loginErrors: string[] = [];
  public loginForm = new FormGroup({
    email: new FormControl(this.loginModel.email, [
      Validators.required,
      Validators.email,
    ]),
    password: new FormControl(this.loginModel.password, [Validators.required]),
  });

  constructor(
    private dialogRef: MatDialogRef<QuickSignUpModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { reason: string, email: string | undefined, pendingAction?: PendingFavourite },
    private sessionService: SessionService,
    private pendingFavourite: PendingFavouriteService
  ) { }

  ngOnInit(): void {
    this.initializeGoogleSignIn();
    this.initializeFacebookSDK();

    if (this.data && this.data.email != undefined)
      setTimeout(() => {
        this.loginForm.get('email').setValue(this.data.email)
        this.registerForm.get('email').setValue(this.data.email)
      }, 10)
  }

  private get signupReason(): string {
    return (this.data && this.data.reason) ? this.data.reason : 'favourite';
  }

  public setLoginPage(loginPage: boolean): void {
    this.registerScreen = !loginPage;
  }

  public login(): void {
    if (this.loginForm.invalid) return;
    this.loginModel = this.loginForm.getRawValue() as LoginModel;

    this.sessionService.login(this.loginModel, (resp: boolean) => {
      if (resp) this.dialogRef.close(true);
    });
  }

  public register(): void {
    if (this.registerForm.invalid) return;
    this.registerModel = this.registerForm.getRawValue() as RegisterModel;

    if (this.data && this.data.reason)
      this.registerModel.signupReason = this.data.reason;

    this.sessionService.register(
      this.registerModel,
      (success: boolean, message: string, errors: string[]) => {
        if (success) this.loginAfterRegister();
        else this.registerErrors = errors.slice(0, 1);
      }
    );
  }

  private loginAfterRegister(): void {
    this.sessionService.login(
      this.sessionService.registerToLogin(this.registerModel),
      (success: boolean, message: string) => {
        if (success) this.dialogRef.close(success);
        else console.error(message);
      }
    );
  }

  // ---- Social sign-in (mirrors the main login page; closes the modal on
  //      success so the original action — e.g. saving a favourite — proceeds) ----

  private initializeGoogleSignIn(): void {
    const checkGoogleLoaded = setInterval(() => {
      if (typeof (window as any).google !== 'undefined') {
        clearInterval(checkGoogleLoaded);
        (window as any).google.accounts.id.initialize({
          client_id: '505978889074-sj1une8r7955ofq2uasjq11g0eia8p6r.apps.googleusercontent.com',
          callback: (response: any) => this.handleGoogleCredentialResponse(response),
          auto_select: false,
        });
      }
    }, 100);
    setTimeout(() => { clearInterval(checkGoogleLoaded); }, 10000);
  }

  public googleLogin(): void {
    this.socialError = '';
    if (typeof (window as any).google === 'undefined') {
      this.socialError = 'Google sign-in is not available. Please refresh and try again.';
      return;
    }
    (window as any).google.accounts.id.prompt();
  }

  private handleGoogleCredentialResponse(response: any): void {
    if (!response || !response.credential) {
      this.socialError = 'Google sign-in was cancelled or failed.';
      return;
    }
    this.sessionService.handleGoogleLogin(
      response.credential,
      this.signupReason,
      (success: boolean, message: string) => {
        if (success) this.dialogRef.close(true);
        else this.socialError = message || 'Google sign-in failed. Please try again.';
      }
    );
  }

  private initializeFacebookSDK(): void {
    const checkFacebookLoaded = setInterval(() => {
      if (typeof (window as any).FB !== 'undefined') {
        clearInterval(checkFacebookLoaded);
        (window as any).FB.init({
          appId: '1163605102162389',
          cookie: true,
          xfbml: true,
          version: 'v21.0',
        });
      }
    }, 100);
    setTimeout(() => { clearInterval(checkFacebookLoaded); }, 10000);
  }

  public facebookLogin(): void {
    this.socialError = '';
    if (typeof (window as any).FB === 'undefined') {
      this.socialError = 'Facebook sign-in is not available. Please refresh and try again.';
      return;
    }
    (window as any).FB.login((response: any) => {
      if (response.authResponse) this.handleFacebookAuthResponse(response.authResponse);
    }, { scope: 'public_profile,email' });
  }

  private handleFacebookAuthResponse(authResponse: any): void {
    const accessToken = authResponse.accessToken;
    if (!accessToken) {
      this.socialError = 'Facebook sign-in failed. Please try again.';
      return;
    }
    this.sessionService.handleFacebookLogin(
      accessToken,
      this.signupReason,
      (success: boolean, message: string) => {
        if (success) this.dialogRef.close(true);
        else this.socialError = message || 'Facebook sign-in failed. Please try again.';
      }
    );
  }

  public appleLogin(): void {
    // Apple is a full-page redirect, so route the return through /login,
    // which has the handler that completes the Apple sign-in. Persist the
    // favourite/notify intent so it can be replayed once the user returns.
    if (this.data && this.data.pendingAction) {
      this.pendingFavourite.set(this.data.pendingAction);
    }
    const authBaseUrl = getOAuthApiOrigin();
    const returnUrl = encodeURIComponent(`${window.location.origin}/login`);
    window.location.href =
      `${authBaseUrl}/auth/apple?signupReason=${encodeURIComponent(this.signupReason)}&returnUrl=${returnUrl}`;
  }

  public closeWithFail(): void {
    this.dialogRef.close(false); // no account created
  }
}
